import React from "react"
import { Box, ButtonBase, Tooltip } from "@mui/material"
import { alpha, keyframes } from "@mui/material/styles"

import IrisMark, { IRIS } from "@components/brand/IrisMark"
import { userData } from "@signals"
import { irisProfile } from "@signals/irisProfile"
import { dsColorNew } from "@palette"
import { isIrisUser } from "Layout/PmsAgentWidget"
import useIrisState from "Layout/useIrisState"
import { chatLang } from "@utils/askIris/lang"
import { askQuestion } from "@utils/askIris/questions"
import { fitBudget } from "@utils/askIris/canvasState"
import { askIris } from "@utils/askIris/send"
import { ASK_TEXT, askButtonState, askTone, askTooltip } from "@utils/askIris/buttonState"

/**
 * "Ask Iris" (CU-869fkdhjv phase 3, UX C1): one control, three forms.
 *
 * - `label` — "Ask Iris" in a chart or modal header and at the end of the "Read:" line;
 * - `block` — the full-width band of the cell detail;
 * - `icon` — the mark alone, on repeated elements (Activity rows, KPI and count cards).
 *
 * Every labelled form says only "Ask Iris"; what it is about ("…about this price", "…about this chart") is the
 * tooltip and the accessible name (Curro 2026-10-28).
 *
 * One click sends (criterion 2): `getAsk()` is called at click time — the evidence of that moment, criterion 5 —
 * and returns `{ kind, labels, subject, state }` (or null: nothing to ask); `onBeforeAsk` runs next (the cell
 * detail closes, C2), then the question goes on the next tick, once a closing popover has given the focus back.
 *
 * Help mode (CU-869fmufdb): the `screen` form (the header icon, mark + "?" badge, no text) asks with
 * `{ kind: 'screen', subject, help }` and no `state`: the screen hint rides on that turn, no evidence is sent.
 * `tooltip` may be a function `(reason | null) => node` for a rich tooltip (the screen's About text, C8), with
 * `tooltipProps` passed to the MUI Tooltip.
 *
 * Its state is the launcher's (`useIrisState` over `Layout/irisState`, C1): not painted for a user without Iris,
 * with `hasData` false or over the tour's demo rows; disabled with its reason while Iris starts, answers or is
 * not available (UX § c).
 *
 * Identity (UX § Button identity, direction B "Bocadillo", C14-C18): the silhouette of Iris's own answer bubble
 * (`irisSkin.js` .dsb-bot, three round corners and a tight bottom-left one) in a soft web-blue→cyan tint. Only the
 * launcher keeps the full gradient; the hover deepens the tint and never borrows the row actions' grey→app-blue
 * (Approve / Discard). Defined once here: pages never restyle it with `sx`.
 */

/** Size of each form and of its mark (UX sizes table, C15). `screen` is the help-mode header icon (CU-869fmufdb). */
export const ASK_IRIS_SIZES = {
	label: { height: 32, mark: IRIS.button },
	block: { height: 36, mark: IRIS.button },
	icon: { height: 30, mark: IRIS.button },
	screen: { height: 36, mark: IRIS.avatar, badge: 14 },
}

// bubble silhouette per form: three equal corners, the tail corner (bottom-left) tight
const corners = (r, tail) => `${r}px ${r}px ${r}px ${tail}px`
const shapes = {
	label: { height: 32, pl: '7px', pr: 1.5, borderRadius: corners(14, 4) },
	block: { height: 36, px: 1.5, width: '100%', fontSize: 13.5, borderRadius: corners(16, 5) },
	icon: { width: 30, height: 30, borderRadius: corners(13, 4) },
	screen: { width: 36, height: 36, borderRadius: corners(15, 4), position: 'relative' },
}

const tint = (blue, cyan) => `linear-gradient(135deg, ${alpha(dsColorNew.webBlue500, blue)} 0%, ${alpha(dsColorNew.cyan, cyan)} 100%), ${dsColorNew.white}`

const breathe = keyframes({ '0%, 100%': { opacity: 0.35 }, '50%': { opacity: 0.7 } })

/**
 * The signature of a form in a tone (`idle` / `wait` / `off`, `askTone`): what the help-mode header icon of
 * CU-869fmufdb reuses as is, with `askIrisBadgeSx` for its "?".
 */
export function askIrisSx(variant = 'label', tone = 'idle') {
	return {
		...shapes[variant],
		display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, flexShrink: 0,
		boxSizing: 'border-box', fontFamily: 'inherit', fontSize: shapes[variant].fontSize || 13, fontWeight: 600,
		lineHeight: 1, whiteSpace: 'nowrap', color: dsColorNew.noteInfo, // blue-700 ink: ~6:1 on the tint (C18)
		background: tint(0.1, 0.12),
		border: `1px solid ${alpha(dsColorNew.webBlue500, 0.14)}`,
		transition: 'background 120ms ease, border-color 120ms ease',
		'&:hover': { background: tint(0.19, 0.22), borderColor: alpha(dsColorNew.webBlue500, 0.38) },
		'&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.webBlue500}`, outlineOffset: 2 },
		...(tone !== 'idle' && { color: dsColorNew.textSubtle, cursor: 'default' }),
		...(tone === 'wait' && {
			background: dsColorNew.pillTrackBg, borderColor: dsColorNew.grayBorder,
			'& .ask-iris-mark': { opacity: 0.45, animation: `${breathe} 1.6s ease-in-out infinite` },
			'@media (prefers-reduced-motion: reduce)': { '& .ask-iris-mark': { animation: 'none' } },
		}),
		...(tone === 'off' && {
			background: dsColorNew.white, border: `1px dashed ${alpha(dsColorNew.textSubtle, 0.5)}`,
			'& .ask-iris-mark': { filter: 'grayscale(1)', opacity: 0.4 },
		}),
	}
}

/** The "?" of the `screen` form (14 px, bottom-right corner); grey while Iris waits or is off. */
export function askIrisBadgeSx(tone = 'idle') {
	const size = ASK_IRIS_SIZES.screen.badge
	return {
		position: 'absolute', right: 0, bottom: 0, width: size, height: size, borderRadius: '50%',
		backgroundColor: tone === 'idle' ? dsColorNew.webBlue500 : dsColorNew.textSubtle, color: dsColorNew.white,
		fontSize: 10, fontWeight: 800, lineHeight: `${size}px`, textAlign: 'center',
		boxShadow: `0 0 0 1.5px ${dsColorNew.white}`,
	}
}

/** The same gate as the launcher: whether this user gets Iris at all (one place for every Ask Iris form). */
export const askIrisAllowed = () => isIrisUser(userData.value)

const browserLang = () => (typeof navigator === 'undefined' ? '' : navigator.language)

export default function AskIrisButton({
	variant = 'label', tooltip, tooltipProps = {}, ariaLabel, getAsk, onBeforeAsk, hasData = true, demo = false, sx = {},
}) {
	const allowed = askIrisAllowed()
	const iris = useIrisState(allowed)
	const look = askButtonState({ isIrisUser: allowed, iris, hasData, demo })
	if (look.mode === 'hidden') return null

	const disabled = look.mode === 'disabled'
	const tone = askTone(look)
	const richTooltip = typeof tooltip === 'function'
	const about = askTooltip(variant, richTooltip ? undefined : tooltip)

	const onClick = e => {
		// rows and cells open on click: asking is not opening (C4)
		e.stopPropagation()
		if (disabled || typeof getAsk !== 'function') return
		const ask = getAsk()
		if (!ask || !(ask.state || ask.help)) return
		const lang = chatLang(irisProfile.value?.lang, browserLang())
		const question = askQuestion(ask.kind, ask.labels, lang, { subject: ask.subject })
		// help mode sends the screen hint and no evidence: nothing to fit; a screen may bring its own budget (Last changes)
		const state = ask.help ? undefined : fitBudget(ask.state, ask.budget).state
		if (typeof onBeforeAsk === 'function') onBeforeAsk()
		setTimeout(() => askIris({ dsb: window.DSB, question, state, help: ask.help }), 0)
	}

	const button = <ButtonBase
		onClick={onClick}
		disabled={disabled}
		aria-label={ariaLabel || about}
		sx={{ ...askIrisSx(variant, tone), ...sx }}
	>
		<Box component="span" className="ask-iris-mark" sx={{ display: 'flex' }}><IrisMark size={ASK_IRIS_SIZES[variant].mark}/></Box>
		{variant === 'screen' && <Box component="span" aria-hidden sx={askIrisBadgeSx(tone)}>?</Box>}
		{variant !== 'icon' && variant !== 'screen' && ASK_TEXT}
	</ButtonBase>

	// MUI needs a wrapper to show a tooltip on a disabled button
	const title = richTooltip ? tooltip(disabled ? look.reason : null) : (disabled ? look.reason : about)
	return <Tooltip title={title} disableInteractive {...tooltipProps}>
		<Box component="span" sx={{ display: variant === 'block' ? 'flex' : 'inline-flex', ...(variant === 'block' && { width: '100%' }) }}>
			{button}
		</Box>
	</Tooltip>
}
